// ui/data/queries.js — the cached reads of the redesigned pages.
//
// One key + one query function per resource. The query functions call the
// SAME services the pages called before (src/services/*), with the same URLs,
// params and payloads; only where the result lives changed (./queryClient.js).
//
// Freshness (staleTime):
//   0      money — halt state + risk limits (/api/risk), balances
//          (/api/portfolio/summary), positions/exposure, the Alpaca account
//          (buying power, account blocks), the scanners' P&L summary. Cached values render first, then re-read on
//          every mount.
//   30 s   everything else (the client default): bots, exchanges, drafts,
//          scanners, backtest lists, histories.
//   5 min  research catalogues / lists (the lake rebuilds them nightly).
//
// Polls: only ONE observer per key sets `refetchInterval` (react-query runs a
// timer per observer): the app shell polls /api/risk (60 s); a page polls it
// only when rendered outside the shell. Polls pause while the tab is hidden.
//
// Invalidation (see `invalidate*` below): a page's own writes call one of
// these, and services/http.js invalidates by URL after EVERY successful write
// (./invalidation.js), so no page shows stale data after an action.
import { useQuery, useQueries } from '@tanstack/react-query';

import riskService from '../../services/riskService';
import tradingBotService from '../../services/tradingBotService';
import exchangeApiService from '../../services/exchangeApiService';
import strategyDraftService from '../../services/strategyDraftService';
import portfolioService from '../../services/portfolioService';
import allocationService from '../../services/allocationService';
import backtestService from '../../services/backtestService';
import scannerApi from '../../services/scannerApi';
import scannerService from '../../services/scannerService';
import { getIdeas } from '../../services/researchService';
import { fetchAlpacaAccountStatus } from '../../hooks/useAlpacaAccountStatus';
import { formatBotRow, mergeBotLists } from '../../containers/Trader_Form/botLive';
import { useAppQueryClient } from './queryClient';
import { qk } from './keys';

export const RISK_POLL_MS = 60 * 1000;
export const ALPACA_POLL_MS = 30 * 1000;
export const RESEARCH_STALE_MS = 5 * 60 * 1000;

/* ── keys (./keys.js) ───────────────────────────────────────────────── */

export { qk };

/** useQuery on the app's client (the provider's, or the singleton in tests). */
export function useAppQuery(options) {
  const client = useAppQueryClient();
  return useQuery(options, client);
}

/** The first load: nothing cached yet and a request in flight. */
export const firstLoad = (q) => Boolean(q && q.isLoading);
/** A background refresh of data that is already on screen. */
export const updating = (q) => Boolean(q && q.isFetching && q.data !== undefined);
/** An HTTP status off a failed query (404 = "this api build has no such endpoint"). */
export const statusOf = (err) => (err && err.response && err.response.status) || null;

// react-query refuses `undefined` as data; the old code treated a missing body
// as "nothing", so do the same.
const orNull = (v) => (v === undefined ? null : v);
const asList = (v) => (Array.isArray(v) ? v : []);

/* ── risk / kill switch ──────────────────────────────────────────────── */

export const fetchRisk = async () => orNull(await riskService.getRisk());

/**
 * GET /api/risk — halt state, limits, today's P&L. staleTime 0 (money).
 * `poll`: set by exactly one observer (the shell; a page only outside it).
 */
export function useRiskQuery({ enabled = true, poll = false } = {}) {
  return useAppQuery({
    queryKey: qk.risk,
    queryFn: fetchRisk,
    enabled,
    staleTime: 0,
    refetchInterval: poll ? RISK_POLL_MS : false,
    refetchIntervalInBackground: false,
  });
}

/* ── bots ────────────────────────────────────────────────────────────── */

const userIdOf = () => parseInt(localStorage.getItem('userId'), 10);

export const fetchBotsRaw = async (status = null) => {
  const list = status
    ? await tradingBotService.list(userIdOf(), { status })
    : await tradingBotService.list(userIdOf());
  // (an older api wrapped the rows as { bots }; ExperimentBlock read both)
  return Array.isArray(list) ? list : asList(list && list.bots);
};

/**
 * The AutoTrader list: regular bots and scanner profiles fetched IN PARALLEL
 * (each leg through its own raw key, so Portfolio's regular list is the same
 * request), merged + shaped exactly as before. A failing profile leg is logged
 * and ignored, as it always was; a failing regular leg fails the query.
 */
export async function fetchBotsMerged(client) {
  const regularP = client.fetchQuery({ queryKey: qk.botsRaw(), queryFn: () => fetchBotsRaw(), staleTime: 0 });
  const profilesP = client.fetchQuery({ queryKey: qk.botsRaw('scanner_profile'), queryFn: () => fetchBotsRaw('scanner_profile'), staleTime: 0 })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('Failed to fetch scanner profiles:', err);
      return [];
    });
  const [regular, profiles] = await Promise.all([regularP, profilesP]);
  // `createdAt` rides along for the "Started" row (formatBotRow drops it).
  return mergeBotLists(profiles, regular).map((raw) => ({ ...formatBotRow(raw), createdAt: raw.created_at || raw.createdAt || null }));
}

export function useBotsMergedQuery({ enabled = true } = {}) {
  const client = useAppQueryClient();
  return useQuery({ queryKey: qk.botsMerged, queryFn: () => fetchBotsMerged(client), enabled }, client);
}

/** Portfolio's list: GET /api/trading_bots as the server returns it. */
export function useBotsRawQuery({ enabled = true } = {}) {
  return useAppQuery({ queryKey: qk.botsRaw(), queryFn: () => fetchBotsRaw(), enabled });
}

export const fetchUserHistories = async () => asList(await tradingBotService.getUserHistories(localStorage.getItem('userId')));

/** GET /api/userhistory/all_by_user — Portfolio Recent activity, the AI advisor. */
export function useUserHistoriesQuery({ enabled = true } = {}) {
  return useAppQuery({ queryKey: qk.userHistories, queryFn: fetchUserHistories, enabled });
}

/** After any bot write (update, pause, delete, create, redeploy, scanner spawn). */
export const invalidateBots = (client) => client.invalidateQueries({ queryKey: qk.bots });

/* ── exchanges / accounts ────────────────────────────────────────────── */

export const fetchExchanges = async () => asList(await exchangeApiService.list());

export function useExchangesQuery({ enabled = true } = {}) {
  return useAppQuery({ queryKey: qk.exchanges, queryFn: fetchExchanges, enabled });
}

/** After connect / edit / lock / remove an account or add / remove a holding. */
export const invalidateAccounts = (client) => Promise.all([
  client.invalidateQueries({ queryKey: qk.exchanges }),
  client.invalidateQueries({ queryKey: qk.portfolio }),
  client.invalidateQueries({ queryKey: qk.alpacaStatus }),
]);

/**
 * GET /api/account/alpaca/status — market hours, account blocks, halts, buying power.
 * staleTime 0 (buying power). The same 30 s poll as useAlpacaAccountStatus.
 */
export function useAlpacaStatusQuery({ enabled = true, poll = true } = {}) {
  const q = useAppQuery({
    queryKey: qk.alpacaStatus,
    queryFn: async () => orNull(await fetchAlpacaAccountStatus()),
    enabled,
    staleTime: 0,
    refetchInterval: poll ? ALPACA_POLL_MS : false,
    refetchIntervalInBackground: false,
  });
  const data = q.data || {};
  // The useAlpacaAccountStatus shape, so consumers did not change.
  return {
    loading: q.isLoading,
    error: q.error ? ((q.error.response && q.error.response.data && q.error.response.data.message) || q.error.message || 'Failed to fetch account status') : null,
    statusByExchangeId: data.accounts || {},
    marketStatus: data.marketStatus || null,
    nextOpen: data.nextOpen || null,
    nextClose: data.nextClose || null,
    haltedSymbols: Array.isArray(data.haltedSymbols) ? data.haltedSymbols : [],
    refresh: q.refetch,
  };
}

/* ── drafts ──────────────────────────────────────────────────────────── */

export const fetchDrafts = async () => asList(await strategyDraftService.listDrafts());

export function useDraftsQuery({ enabled = true } = {}) {
  return useAppQuery({ queryKey: qk.drafts, queryFn: fetchDrafts, enabled });
}

export const invalidateDrafts = (client) => client.invalidateQueries({ queryKey: qk.drafts });

/* ── portfolio ───────────────────────────────────────────────────────── */

export const fetchPortfolioSummary = async ({ fresh = false } = {}) => orNull(await portfolioService.summary({ fresh }));

/**
 * The wizards' per-account figures off the summary:
 * `{ [exchangeId]: { balance, assets: { USDT: 1234, ... } } }`.
 */
export function balancesOf(summary) {
  const out = {};
  ((summary && summary.exchanges) || []).forEach((e) => {
    const assets = {};
    (e.holdings || []).forEach((h) => {
      const k = String(h.symbol || '').toUpperCase();
      assets[k] = (assets[k] || 0) + (Number(h.value) || 0);
    });
    const bal = Number(e.balance);
    out[e.id] = { balance: Number.isFinite(bal) ? bal : null, assets };
  });
  return out;
}

/** GET /api/portfolio/summary — balances. staleTime 0. */
export function usePortfolioSummaryQuery({ enabled = true } = {}) {
  return useAppQuery({ queryKey: qk.portfolioSummary, queryFn: () => fetchPortfolioSummary(), enabled, staleTime: 0 });
}

/**
 * Re-read a key with a DIFFERENT request (`?fresh=1`). A plain re-read of
 * the key may already be in flight (a write elsewhere invalidated it), and
 * fetchQuery would join that one — returning the server-cached copy without
 * the fresh flag. So it is cancelled first.
 */
async function refetchWith(client, queryKey, queryFn) {
  await client.cancelQueries({ queryKey, exact: true });
  return client.fetchQuery({ queryKey, queryFn, staleTime: 0 });
}

/** The Refresh button: the same key, re-read with `?fresh=1` (bypasses the server cache). */
export const refreshPortfolioSummary = (client) => refetchWith(client, qk.portfolioSummary, () => fetchPortfolioSummary({ fresh: true }));

export function usePortfolioHistoryQuery(range, scope, { enabled = true } = {}) {
  return useAppQuery({
    queryKey: qk.portfolioHistory(range, scope),
    queryFn: async () => orNull(await portfolioService.history(range, scope)),
    enabled,
  });
}

/** GET /api/portfolio/exposure[?exchange_id] — positions. staleTime 0. */
export function useExposureQuery(scope, { enabled = true } = {}) {
  return useAppQuery({
    queryKey: qk.exposure(scope),
    queryFn: async () => orNull(await portfolioService.exposure(scope, { fresh: false })),
    enabled,
    staleTime: 0,
    // NO placeholderData: another account's positions / beta must never show
    // under a newly selected scope. A new scope gets its own first-load state;
    // a revisit of a scope still renders its own cached copy.
  });
}

/** Exposure re-read with `?fresh=1`, into the same key. */
export const refreshExposure = (client, scope) => refetchWith(
  client,
  qk.exposure(scope),
  async () => orNull(await portfolioService.exposure(scope, { fresh: true })),
);

export function useAllocationQuery({ enabled = true } = {}) {
  return useAppQuery({ queryKey: qk.allocation, queryFn: async () => orNull(await allocationService.getAllocation()), enabled });
}

/* ── scanners ────────────────────────────────────────────────────────── */

export const fetchScannerProfiles = async () => asList(await scannerApi.scannerProfiles.list());

export function useScannerProfilesQuery({ enabled = true } = {}) {
  return useAppQuery({ queryKey: qk.scannerProfiles, queryFn: fetchScannerProfiles, enabled });
}

/** One profile's spawned bots; a failed read resolves to null ("unknown"), as before. */
const spawnedQuery = (id) => ({
  queryKey: qk.scannerSpawned(id),
  queryFn: () => scannerApi.scannerProfiles.spawned(id, 50).then((bots) => bots || [], () => null),
});

/**
 * One spawned-bots read per profile (as before), for the list rows' "N bots
 * open" / "N today": { [profileId]: rows | null } (only the ones answered).
 * Each is its own cache entry, so the open scanner's detail reuses it.
 */
export function useScannerSpawnedMap(profiles) {
  const client = useAppQueryClient();
  const ids = (profiles || []).map((p) => p.id);
  return useQueries({
    queries: ids.map((id) => spawnedQuery(id)),
    combine: (results) => {
      const out = {};
      results.forEach((r, i) => { if (r.data !== undefined) out[ids[i]] = r.data; });
      return out;
    },
  }, client);
}

export function useScannerSpawnedQuery(id) {
  return useAppQuery(spawnedQuery(id));
}

/**
 * GET /api/scanner/pnl-summary[?profileId=] — the scanners' lifetime P&L
 * (AutoTrader's scanner rows, the scanner sheet's "Since start"). staleTime 0
 * (money). A 404 (an older api) is not retried (retryPolicy); the callers fall
 * back to the bots on the page.
 */
export function useScannerPnlQuery({ profileId = null, enabled = true } = {}) {
  return useAppQuery({
    queryKey: qk.scannerPnl(profileId),
    queryFn: async () => orNull(await scannerService.getPnlSummary(profileId)),
    enabled,
    staleTime: 0,
  });
}

/** GET /api/scanner/profiles/:id — the counters of one scanner. */
export function useScannerProfileQuery(id) {
  return useAppQuery({ queryKey: qk.scannerProfile(id), queryFn: async () => orNull(await scannerService.getProfile(id)) });
}

/** GET /api/scanner/hits with these params (one page). */
export function useScannerHitsQuery(params, { enabled = true } = {}) {
  return useAppQuery({ queryKey: qk.scannerHits(params), queryFn: () => scannerService.listHits(params), enabled });
}

export const SCANNER_HEALTH_POLL_MS = 30 * 1000;

/** GET /api/scanner/health, polled every 30 s while the Scanners area is open. */
export function useScannerHealthQuery() {
  return useAppQuery({
    queryKey: qk.scannerHealth,
    queryFn: async () => orNull(await scannerService.getHealth()),
    refetchInterval: SCANNER_HEALTH_POLL_MS,
    refetchIntervalInBackground: false,
  });
}

/** After create / edit / pause / delete / unpause a scanner (they are bots too). */
export const invalidateScanners = (client) => Promise.all([
  client.invalidateQueries({ queryKey: qk.scanners }),
  client.invalidateQueries({ queryKey: qk.bots }),
]);

/* ── backtests ───────────────────────────────────────────────────────── */

export const fetchBacktestActive = async () => {
  const r = await backtestService.getActive();
  return r && r.active ? r : null;
};

export function useBacktestActiveQuery({ enabled = true } = {}) {
  return useAppQuery({ queryKey: qk.backtestActive, queryFn: fetchBacktestActive, enabled, staleTime: 0 });
}

/** Poll the Running list every 5 s while something runs, every 60 s otherwise. */
export const RUNNING_POLL_MS = 5 * 1000;
export const RUNNING_IDLE_MS = 60 * 1000;
export const runningInterval = (data) => (Array.isArray(data) && data.length ? RUNNING_POLL_MS : RUNNING_IDLE_MS);

export const fetchRunningJobs = async () => asList(await backtestService.getRunning());

/**
 * GET /api/backtest/active `jobs` — every queued / running test. `poll`: set
 * by exactly one observer (the shell's Running indicator); pages that only
 * read it (BookTest's re-attach) share the same cached list.
 */
export function useRunningJobsQuery({ enabled = true, poll = false } = {}) {
  return useAppQuery({
    queryKey: qk.backtestRunning,
    queryFn: fetchRunningJobs,
    enabled,
    staleTime: poll ? 0 : 5 * 1000,
    refetchInterval: poll ? (query) => runningInterval(query && query.state && query.state.data) : false,
    refetchIntervalInBackground: false,
  });
}

export const fetchBacktestResults = async (limit) => asList(await backtestService.getResults(limit));

/** GET /api/backtest/recent?limit= (saved runs). `staleTime: 0` re-reads on every open. */
export function useBacktestResultsQuery(limit, { enabled = true, staleTime } = {}) {
  const opts = { queryKey: qk.backtestResults(limit), queryFn: () => fetchBacktestResults(limit), enabled };
  // (an explicit `staleTime: undefined` would override the 30 s default)
  if (staleTime !== undefined) opts.staleTime = staleTime;
  return useAppQuery(opts);
}

/** After a backtest is saved / deleted / submitted / cancelled. */
export const invalidateBacktests = (client) => client.invalidateQueries({ queryKey: qk.backtest });

/* ── research ────────────────────────────────────────────────────────── */

/**
 * A research read (catalogue, list, board) through the cache, 5 min fresh:
 * the lake rebuilds these nightly. `name` + `params` form the key, so pass
 * every input of `fn` in `params`. The PAGE passes `fn` (its own service
 * call), so the request is exactly the one it made before.
 */
/**
 * `keepPrevious`: keep the previous params' result on screen (dimmed by the
 * page) while new params load. ONLY for public, non-account data (the
 * leaderboard, insider filings) — never for anything tied to an account.
 */
export function useResearchQuery(name, fn, { params, enabled = true, keepPrevious = false } = {}) {
  return useAppQuery({
    queryKey: params === undefined ? [...qk.research, name] : [...qk.research, name, params],
    queryFn: async () => orNull(await fn()),
    enabled,
    staleTime: RESEARCH_STALE_MS,
    placeholderData: keepPrevious ? (prev) => prev : undefined,
  });
}

/** The nightly /ideas feed (AutoTrader's "Ideas for your next bot"). */
export function useIdeasQuery({ enabled = true } = {}) {
  return useAppQuery({ queryKey: [...qk.research, 'ideas'], queryFn: async () => orNull(await getIdeas()), enabled, staleTime: RESEARCH_STALE_MS });
}

/* ── risk writes ─────────────────────────────────────────────────────── */

/** After halt / resume: the halt state everywhere. Flatten also moves positions. */
export const invalidateRisk = (client, { positions = false } = {}) => Promise.all([
  client.invalidateQueries({ queryKey: qk.risk }),
  positions ? client.invalidateQueries({ queryKey: qk.portfolio }) : null,
  positions ? client.invalidateQueries({ queryKey: qk.bots }) : null,
]);
